import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { I18nProvider } from "@/contexts/I18nContext";
import { Toaster } from "sonner";
import ChatbotWidget from "@/components/ChatbotWidget";
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import BuyerIntakePage from "@/pages/BuyerIntakePage";
import SellerIntakePage from "@/pages/SellerIntakePage";
import DashboardPage from "@/pages/DashboardPage";
import AdminPage from "@/pages/AdminPage";
import DealDetailPage from "@/pages/DealDetailPage";
import CreditRepairPage from "@/pages/CreditRepairPage";
import CreditRepairIntakePage from "@/pages/CreditRepairIntakePage";
import NegotiationRoomPage from "@/pages/NegotiationRoomPage";
import MortgagePage from "@/pages/MortgagePage";
import PaymentSuccessPage from "@/pages/PaymentSuccessPage";
import CRMPage from "@/pages/CRMPage";
import MarketplacePage from "@/pages/MarketplacePage";
import AgentPortalPage from "@/pages/AgentPortalPage";
import OperatorDashboardPage from "@/pages/OperatorDashboardPage";
import TemplatesPage from "@/pages/TemplatesPage";
import AnalyticsPage from "@/pages/AnalyticsPage";
import GuidePage from "@/pages/GuidePage";
import OpportunityFeedPage from "@/pages/OpportunityFeedPage";
import LegalPage from "@/pages/LegalPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";

function ProtectedRoute({ children, allowedRoles }) {
    const { user, loading } = useAuth();
    if (loading) return (
        <div className="min-h-screen flex items-center justify-center bg-zinc-50">
            <div className="w-8 h-8 border-2 border-emerald-700 border-t-transparent rounded-full animate-spin" />
        </div>
    );
    if (!user) return <Navigate to="/login" />;
    if (allowedRoles && !allowedRoles.includes(user.role)) return <Navigate to="/dashboard" />;
    return children;
}

function AppRoutes() {
    const { user, loading } = useAuth();
    if (loading) return (
        <div className="min-h-screen flex items-center justify-center bg-zinc-50">
            <div className="w-8 h-8 border-2 border-emerald-700 border-t-transparent rounded-full animate-spin" />
        </div>
    );

    const getHomeRedirect = () => {
        if (!user) return '/login';
        if (user.role === 'admin') return '/admin';
        if (user.role === 'agent') return '/agent';
        if (user.role === 'operator') return '/operator';
        return '/dashboard';
    };

    return (
        <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={user ? <Navigate to={getHomeRedirect()} /> : <LoginPage />} />
            <Route path="/register" element={user ? <Navigate to={getHomeRedirect()} /> : <RegisterPage />} />
            <Route path="/marketplace" element={<MarketplacePage />} />
            <Route path="/guide" element={<GuidePage />} />
            <Route path="/legal" element={<LegalPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/buyer/intake" element={<ProtectedRoute allowedRoles={['buyer']}><BuyerIntakePage /></ProtectedRoute>} />
            <Route path="/seller/intake" element={<ProtectedRoute allowedRoles={['seller']}><SellerIntakePage /></ProtectedRoute>} />
            <Route path="/dashboard" element={<ProtectedRoute allowedRoles={['buyer', 'seller']}><DashboardPage /></ProtectedRoute>} />
            <Route path="/admin" element={<ProtectedRoute allowedRoles={['admin']}><AdminPage /></ProtectedRoute>} />
            <Route path="/agent" element={<ProtectedRoute allowedRoles={['agent', 'admin']}><AgentPortalPage /></ProtectedRoute>} />
            <Route path="/operator" element={<ProtectedRoute allowedRoles={['operator', 'admin']}><OperatorDashboardPage /></ProtectedRoute>} />
            <Route path="/deals/:dealId" element={<ProtectedRoute><DealDetailPage /></ProtectedRoute>} />
            <Route path="/deals/:dealId/negotiate" element={<ProtectedRoute><NegotiationRoomPage /></ProtectedRoute>} />
            <Route path="/credit-repair" element={<ProtectedRoute><CreditRepairPage /></ProtectedRoute>} />
            <Route path="/credit-repair/intake" element={<ProtectedRoute><CreditRepairIntakePage /></ProtectedRoute>} />
            <Route path="/credit-repair/payment-success" element={<ProtectedRoute><PaymentSuccessPage /></ProtectedRoute>} />
            <Route path="/payment/success" element={<ProtectedRoute><PaymentSuccessPage /></ProtectedRoute>} />
            <Route path="/payment/cancel" element={<ProtectedRoute><PaymentSuccessPage /></ProtectedRoute>} />
            <Route path="/mortgage" element={<ProtectedRoute><MortgagePage /></ProtectedRoute>} />
            <Route path="/crm" element={<ProtectedRoute><CRMPage /></ProtectedRoute>} />
            <Route path="/templates" element={<ProtectedRoute allowedRoles={['admin']}><TemplatesPage /></ProtectedRoute>} />
            <Route path="/analytics" element={<ProtectedRoute allowedRoles={['admin']}><AnalyticsPage /></ProtectedRoute>} />
            <Route path="/opportunities" element={<ProtectedRoute allowedRoles={['admin', 'operator']}><OpportunityFeedPage /></ProtectedRoute>} />
            <Route path="*" element={<Navigate to="/" />} />
        </Routes>
    );
}

function App() {
    return (
        <BrowserRouter>
            <I18nProvider>
                <AuthProvider>
                    <AppRoutes />
                    <ChatbotWidget />
                    <Toaster richColors position="top-right" />
                </AuthProvider>
            </I18nProvider>
        </BrowserRouter>
    );
}

export default App;
